import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import SectionCard from '@/components/base/SectionCard';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import DangerConfirm from '@/components/base/DangerConfirm';
import { useSystemInfo } from '@/hooks/useSystemInfo';
import { listUsers } from '@/lib/userService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/ToastProvider';
import { purgeReceiptHistory, purgeProjectEventHistory } from '@/lib/maintenance';
import { logAudit } from '@/lib/audit';
import { formatCurrency } from '@/lib/format';
import { BRAND_NAME } from '@/lib/brand';
import type { SystemHealth } from '@/types';

const HEALTH_TONE: Record<SystemHealth, { tone: 'success' | 'warning' | 'danger'; icon: string; label: string }> = {
  operativo: { tone: 'success', icon: 'ri-checkbox-circle-line', label: 'Operativo' },
  advertencia: { tone: 'warning', icon: 'ri-alert-line', label: 'Advertencia' },
  error: { tone: 'danger', icon: 'ri-close-circle-line', label: 'Error' },
};

interface MetricCardProps {
  icon: string;
  title: string;
  accent: string;
  children: React.ReactNode;
}

function MetricCard({ icon, title, accent, children }: MetricCardProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex items-center gap-2.5">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${accent}`}>
          <i className={`${icon} text-lg`}></i>
        </span>
        <p className="font-heading text-sm font-bold text-foreground-950">{title}</p>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-background-200 py-1.5 last:border-0">
      <span className="text-xs text-foreground-500">{label}</span>
      <span className="text-sm font-semibold text-foreground-900">{value}</span>
    </div>
  );
}

export default function SettingsPage() {
  const { isSuperUsuario, profileLoading, profile } = useAuth();
  const { notify } = useToast();
  const { info, health, loading, error, reload } = useSystemInfo();

  const [userStats, setUserStats] = useState<{ total: number; active: number }>({ total: 0, active: 0 });
  const [firstConfirm, setFirstConfirm] = useState(false);
  const [secondConfirm, setSecondConfirm] = useState(false);
  const [purging, setPurging] = useState(false);

  const [eventFirstConfirm, setEventFirstConfirm] = useState(false);
  const [eventSecondConfirm, setEventSecondConfirm] = useState(false);
  const [purgingEvents, setPurgingEvents] = useState(false);

  const loadUsers = useCallback(async () => {
    const { data } = await listUsers();
    if (data) {
      setUserStats({
        total: data.users.length,
        active: data.users.filter((u) => u.status === 'ACTIVO').length,
      });
    }
  }, []);

  useEffect(() => {
    if (isSuperUsuario) loadUsers();
  }, [isSuperUsuario, loadUsers]);

  const handlePurge = async () => {
    setPurging(true);
    try {
      await purgeReceiptHistory();
      await logAudit({
        userId: profile?.id ?? null,
        userName: profile?.full_name || profile?.username || 'Usuario',
        action: 'LIMPIEZA_HISTORIAL_RECIBOS',
        module: 'Sistema',
        recordId: null,
        description: 'Se eliminó permanentemente el historial de recibos y los ingresos asociados.',
      });
      notify({
        tone: 'success',
        title: 'Historial eliminado',
        message: 'El historial de recibos fue eliminado permanentemente.',
      });
      setSecondConfirm(false);
      reload();
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo completar la limpieza',
        message: 'Ocurrió un problema al eliminar el historial. Intente nuevamente.',
      });
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeEvents = async () => {
    setPurgingEvents(true);
    try {
      await purgeProjectEventHistory();
      await logAudit({
        userId: profile?.id ?? null,
        userName: profile?.full_name || profile?.username || 'Usuario',
        action: 'LIMPIEZA_HISTORIAL_PROYECTOS',
        module: 'Sistema',
        recordId: null,
        description: 'Se eliminó permanentemente el historial de cambios de los proyectos.',
      });
      notify({
        tone: 'success',
        title: 'Historial eliminado',
        message: 'El historial de cambios de los proyectos fue eliminado permanentemente.',
      });
      setEventSecondConfirm(false);
      reload();
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo completar la limpieza',
        message: 'Ocurrió un problema al eliminar el historial. Intente nuevamente.',
      });
    } finally {
      setPurgingEvents(false);
    }
  };

  if (profileLoading) {
    return <Loading label="Verificando permisos..." />;
  }

  if (!isSuperUsuario) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Información del Sistema */}
      <SectionCard
        title="Información del Sistema"
        icon="ri-information-line"
        action={
          <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={reload}>
            Actualizar
          </Button>
        }
      >
        {loading ? (
          <Loading label="Obteniendo información del sistema..." />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : info ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon="ri-folders-line" title="Proyectos" accent="bg-primary-100 text-primary-700">
              <p className="font-heading text-2xl font-extrabold text-foreground-950">
                {info.projects.total}
              </p>
              <div className="mt-2">
                <MetricRow label="Activos" value={String(info.projects.active)} />
                <MetricRow label="En proceso" value={String(info.projects.progress)} />
                <MetricRow label="Desactivados" value={String(info.projects.inactive)} />
              </div>
            </MetricCard>

            <MetricCard icon="ri-file-list-3-line" title="Facturación" accent="bg-accent-100 text-accent-700">
              <p className="font-heading text-2xl font-extrabold text-foreground-950">
                {info.billing.receipts}
              </p>
              <div className="mt-2">
                <MetricRow label="Total facturado" value={formatCurrency(info.billing.total)} />
              </div>
            </MetricCard>

            <MetricCard icon="ri-line-chart-line" title="Finanzas" accent="bg-secondary-100 text-secondary-700">
              <p className="font-heading text-2xl font-extrabold text-foreground-950">
                {formatCurrency(info.finance.balance)}
              </p>
              <div className="mt-2">
                <MetricRow label="Ingresos" value={formatCurrency(info.finance.income)} />
                <MetricRow label="Egresos" value={formatCurrency(info.finance.expense)} />
              </div>
            </MetricCard>

            <MetricCard icon="ri-group-line" title="Usuarios" accent="bg-background-100 text-foreground-600">
              <p className="font-heading text-2xl font-extrabold text-foreground-950">
                {userStats.total}
              </p>
              <div className="mt-2">
                <MetricRow label="Usuarios activos" value={String(userStats.active)} />
              </div>
            </MetricCard>
          </div>
        ) : null}
      </SectionCard>

      {/* Estado del sistema */}
      <SectionCard title="Estado del sistema" icon="ri-pulse-line">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {health.map((item) => {
            const config = HEALTH_TONE[item.status];
            return (
              <div
                key={item.key}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-foreground-950">{item.label}</p>
                  <div className="mt-1">
                    <Badge tone={config.tone} icon={config.icon}>
                      {config.label}
                    </Badge>
                  </div>
                  <p className="mt-1.5 text-xs text-foreground-500">{item.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Administración de usuarios */}
      <SectionCard title="Administración" icon="ri-admin-line">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-group-line text-xl"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Gestión de usuarios</p>
              <p className="text-sm text-foreground-600">
                Cree, edite, active o desactive usuarios y administre sus contraseñas.
              </p>
            </div>
          </div>
          <Link
            to="/usuarios"
            className="inline-flex h-10 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line text-base"></i>
            </span>
            Ir a Usuarios
          </Link>
        </div>
      </SectionCard>

      {/* Limpieza de datos históricos */}
      <section className="rounded-lg border border-red-200 bg-red-50/50 p-4 md:p-5">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-red-100 text-red-600">
            <i className="ri-delete-bin-7-line text-sm"></i>
          </span>
          <h3 className="font-heading text-sm font-bold text-red-800">
            Limpieza de Datos Históricos
          </h3>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-red-100 text-red-600">
            <i className="ri-error-warning-line text-base"></i>
          </span>
          <p className="text-sm text-red-700">
            Estas acciones eliminan permanentemente datos históricos. Esta acción es{' '}
            <strong>irreversible</strong>. Utilice esta opción solo cuando sea estrictamente
            necesario.
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground-900">Eliminar historial de recibos</p>
            <p className="text-xs text-foreground-600">
              Elimina todos los recibos emitidos y los ingresos generados a partir de ellos.
            </p>
          </div>
          <Button variant="danger" icon="ri-delete-bin-6-line" onClick={() => setFirstConfirm(true)}>
            Eliminar historial de recibos
          </Button>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-red-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground-900">
              Eliminar historial de cambios del proyecto
            </p>
            <p className="text-xs text-foreground-600">
              Elimina únicamente el historial de modificaciones realizadas sobre los proyectos. Los
              proyectos actuales, clientes, contratos, recibos y movimientos no serán eliminados.
            </p>
          </div>
          <Button variant="danger" icon="ri-history-line" onClick={() => setEventFirstConfirm(true)}>
            Eliminar historial de cambios
          </Button>
        </div>
      </section>

      {/* Acerca de */}
      <SectionCard title="Acerca de" icon="ri-information-2-line">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <MetricRow label="Nombre" value="IZZY Corporation — Sistema de Seguimiento" />
            <MetricRow label="Empresa" value={BRAND_NAME} />
            <MetricRow label="Versión" value="v1.0.0" />
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-sm leading-relaxed text-foreground-600">
              Sistema interno para la gestión y seguimiento de proyectos, clientes, contratos,
              facturación y finanzas de IZZY Corporation.
            </p>
            <p className="text-xs text-foreground-500">Sistema desarrollado para {BRAND_NAME}.</p>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-server-line text-xl"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Servidor privado</p>
              <p className="text-xs text-foreground-600">
                Acceda al panel del servidor privado de {BRAND_NAME}.
              </p>
            </div>
          </div>
          <a
            href="https://admin.dominioizzycorp.cloud/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-10 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 bg-secondary-50 px-4 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-100"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-external-link-line text-base"></i>
            </span>
            Acceder al servidor privado
          </a>
        </div>
      </SectionCard>

      <ConfirmDialog
        open={firstConfirm}
        title="Esta acción eliminará permanentemente el historial de recibos."
        description="Los datos eliminados no podrán recuperarse. ¿Desea continuar?"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
        tone="danger"
        icon="ri-delete-bin-7-line"
        onConfirm={() => {
          setFirstConfirm(false);
          setSecondConfirm(true);
        }}
        onCancel={() => setFirstConfirm(false)}
      />

      <DangerConfirm
        open={secondConfirm}
        title="¿Está completamente seguro de que desea continuar?"
        description="Se eliminará permanentemente el historial de recibos y los ingresos asociados."
        confirmationWord="ELIMINAR"
        confirmLabel="Eliminar historial"
        cancelLabel="Cancelar"
        loading={purging}
        onConfirm={handlePurge}
        onCancel={() => setSecondConfirm(false)}
      />

      <ConfirmDialog
        open={eventFirstConfirm}
        title="Esta acción eliminará permanentemente el historial de cambios realizados en los proyectos."
        description="Los proyectos actuales no serán eliminados. Esta acción no se puede deshacer. ¿Desea continuar?"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
        tone="danger"
        icon="ri-history-line"
        onConfirm={() => {
          setEventFirstConfirm(false);
          setEventSecondConfirm(true);
        }}
        onCancel={() => setEventFirstConfirm(false)}
      />

      <DangerConfirm
        open={eventSecondConfirm}
        title="¿Está completamente seguro de que desea continuar?"
        description="Se eliminará permanentemente el historial de cambios de los proyectos."
        confirmationWord="ELIMINAR"
        confirmLabel="Eliminar historial"
        cancelLabel="Cancelar"
        loading={purgingEvents}
        onConfirm={handlePurgeEvents}
        onCancel={() => setEventSecondConfirm(false)}
      />
    </div>
  );
}